import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PieChart, BarChart3, AlertTriangle, ArrowRight, Shield, Target, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart as RePieChart, Pie,
} from 'recharts';
import api from '@/lib/api';

import { AnalyticsShell } from '@/components/AnalyticsShell';

const COLORS = ['#0097A7', '#FF3B30', '#FF9500', '#2E7D32', '#5856D6', '#AF52DE', '#007AFF', '#FF2D55', '#8E8E93', '#00C7BE', '#E65100', '#795548', '#4E6974', '#9C27B0', '#3F51B5'];

const HHI_THRESHOLDS = { low: { max: 1500, label: 'Low', color: '#2E7D32' }, moderate: { max: 3000, label: 'Moderate', color: '#FF9500' }, high: { max: 10001, label: 'High', color: '#FF3B30' } };

const ConcentrationPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getConcentrationAnalysis()
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <AnalyticsShell><div className="text-center py-20 text-[#4E6974]">Analysing supplier concentration...</div></AnalyticsShell>;
  if (!data?.has_data) return <AnalyticsShell><EmptyState /></AnalyticsShell>;

  const hhiInfo = data.hhi_country > 3000 ? HHI_THRESHOLDS.high : data.hhi_country > 1500 ? HHI_THRESHOLDS.moderate : HHI_THRESHOLDS.low;
  const countryPieData = data.country_concentration.map((c, i) => ({ name: c.country_name, value: c.spend_pct, fill: COLORS[i % COLORS.length] }));

  return (
    <AnalyticsShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 mb-6">
        <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="concentration-title">Supplier concentration analysis</h1>
        <p className="text-[#4E6974] mt-2">Identify single-source risks and over-dependence on specific countries or sectors.</p>
      </motion.div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard icon={<Target className="h-5 w-5" />} label="Total suppliers" value={data.total_suppliers} testId="conc-total-suppliers" />
        <SummaryCard icon={<PieChart className="h-5 w-5" />} label="Country HHI" value={data.hhi_country.toLocaleString()} sub={hhiInfo.label + ' concentration'} color={hhiInfo.color} testId="conc-hhi" />
        <SummaryCard icon={<AlertTriangle className="h-5 w-5" />} label="Top country share" value={`${data.top_country_pct}%`} sub={data.country_concentration[0]?.country_name} color={data.top_country_pct > 50 ? '#FF3B30' : '#FF9500'} testId="conc-top-country" />
        <SummaryCard icon={<Shield className="h-5 w-5" />} label="Single-source risks" value={data.single_source_risks.length} color={data.single_source_risks.length > 5 ? '#FF3B30' : '#2E7D32'} testId="conc-single-source" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Country spend pie */}
        <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Spend by country</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]" data-testid="country-pie-chart">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie data={countryPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }) => `${name} ${value}%`} labelLine={false} fontSize={10}>
                    {countryPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => [`${v}%`, 'Spend share']} />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Sector spend bar */}
        <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Spend by sector</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]" data-testid="sector-bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.sector_concentration} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis type="number" fontSize={11} tick={{ fill: '#4E6974' }} tickFormatter={v => `${v}%`} />
                  <YAxis type="category" dataKey="sector" fontSize={11} tick={{ fill: '#4E6974' }} width={160} />
                  <Tooltip formatter={v => [`${v}%`, 'Spend share']} />
                  <Bar dataKey="spend_pct" fill="#0097A7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Country breakdown table */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Country concentration breakdown</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="country-table">
              <thead>
                <tr className="border-b border-[#E0E0E0]">
                  <th className="text-left py-3 px-4 text-[#4E6974] font-medium">Country</th>
                  <th className="text-right py-3 px-4 text-[#4E6974] font-medium">Suppliers</th>
                  <th className="text-right py-3 px-4 text-[#4E6974] font-medium">Spend (AUD)</th>
                  <th className="text-right py-3 px-4 text-[#4E6974] font-medium">Share</th>
                  <th className="py-3 px-4 text-[#4E6974] font-medium">Concentration</th>
                </tr>
              </thead>
              <tbody>
                {data.country_concentration.map((c, i) => (
                  <tr key={c.country_code} className="border-b border-[#F5F5F5] hover:bg-[#F9FAFB] transition-colors">
                    <td className="py-3 px-4 font-medium text-[#0D3B4F]">
                      <Link to={`/countries/${c.country_code}`} className="hover:text-[#0097A7]">{c.country_name}</Link>
                    </td>
                    <td className="py-3 px-4 text-right text-[#4E6974]">{c.count}</td>
                    <td className="py-3 px-4 text-right text-[#0D3B4F]">${(c.spend / 1e6).toFixed(1)}M</td>
                    <td className="py-3 px-4 text-right font-semibold text-[#0D3B4F]">{c.spend_pct}%</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#E0E0E0] rounded-full overflow-hidden max-w-[120px]">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, c.spend_pct)}%`, backgroundColor: c.spend_pct > 40 ? '#FF3B30' : c.spend_pct > 20 ? '#FF9500' : '#2E7D32' }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Single source risks */}
      {data.single_source_risks.length > 0 && (
        <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[#FF3B30]" /> Single-source-of-failure risks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="single-source-table">
                <thead>
                  <tr className="border-b border-[#E0E0E0]">
                    <th className="text-left py-3 px-4 text-[#4E6974] font-medium">Commodity / HS code</th>
                    <th className="text-left py-3 px-4 text-[#4E6974] font-medium">Sole supplier</th>
                    <th className="text-left py-3 px-4 text-[#4E6974] font-medium">Country</th>
                    <th className="text-right py-3 px-4 text-[#4E6974] font-medium">Spend</th>
                    <th className="text-left py-3 px-4 text-[#4E6974] font-medium">Alternatives</th>
                  </tr>
                </thead>
                <tbody>
                  {data.single_source_risks.slice(0, 15).map((r, i) => (
                    <tr key={i} className="border-b border-[#F5F5F5] hover:bg-[#F9FAFB]">
                      <td className="py-3 px-4 text-[#0D3B4F] font-medium">{r.commodity}</td>
                      <td className="py-3 px-4 text-[#4E6974]">{r.supplier_name}</td>
                      <td className="py-3 px-4 text-[#4E6974]">{r.country_name}</td>
                      <td className="py-3 px-4 text-right text-[#0D3B4F]">${(r.annual_spend / 1e6).toFixed(1)}M</td>
                      <td className="py-3 px-4">
                        {r.alternatives.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {r.alternatives.map(([code, name]) => (
                              <Badge key={code} variant="outline" className="text-[10px] border-[#0097A7] text-[#0097A7]">{name}</Badge>
                            ))}
                          </div>
                        ) : <span className="text-[#4E6974] text-xs">No mapped alternatives</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-[#0097A7]" /> Diversification recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3" data-testid="concentration-recommendations">
            {data.recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F5F5F5]">
                <ArrowRight className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#0D3B4F]">{r}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </AnalyticsShell>
  );
};

const SummaryCard = ({ icon, label, value, sub, color, testId }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
    <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2 text-[#4E6974]">{icon}<span className="text-xs uppercase tracking-wide">{label}</span></div>
        <p className="text-3xl font-bold" style={{ color: color || '#0D3B4F' }}>{value}</p>
        {sub && <p className="text-xs text-[#4E6974] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  </motion.div>
);

const EmptyState = () => (
  <div className="text-center py-20">
    <PieChart className="h-12 w-12 text-[#4E6974] mx-auto mb-4" />
    <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">No supplier data</h2>
    <p className="text-[#4E6974] mb-6">Upload suppliers to view concentration analysis.</p>
    <Link to="/suppliers"><Button className="bg-[#00707C] hover:bg-[#00707C]/90">Go to suppliers</Button></Link>
  </div>
);

export default ConcentrationPage;
